import { type Container, Graphics, type Sprite } from 'pixi.js';
import { CIRCADIEN, type CircadianAttack } from '../../data/full/circadien';
import { smoothstep } from '../../shared/scalar';
import type { Tempo } from '../../shared/tempo';
import { crescentWay, skyOf, starCount } from '../../sim/full/attacks/circadian';
import { readClock } from '../../sim/full/clock';
import type { PlayerState } from '../../sim/state';
import type { ClassDrawing, PaintedDrawing } from '../drawings';
import { type Shape, TAU, paintSheet } from '../paint';
import type { Look } from '../player-look';
import type { Textures } from '../textures';
import { REFERENCE_RADIUS } from '../textures-looks';
import { add, hide, placeOutline, setTint } from '../util';
import { CIRCADIEN_PIECES, CRESCENT, HANDS, HEAD, HEADDRESS, UNIT } from './circadien-paint';

type Parts = Readonly<Record<keyof typeof CIRCADIEN_PIECES, Shape>>;

const EXTENT = 36;
const MIDDLE = 1;
export const SUN_TINT = 0xffc94d;
export const MOON_TINT = 0xb9c2ff;
const TURN_TICKS = { quick: 8, calm: 24 };
const MOON_TICKS = 6;
const SAMPLES = 8;
const CRESCENT_GLOW = 0.55;
const MAX_CRESCENTS = 12;
// Right of him on the screen, clear of the arcs of his life and his dash.
const DIAL_AT = { x: 34, y: 0, ring: 9.6, width: 1.7 };
const PORTRAIT_HALF_BARS = 1;
const PORTRAIT_ORBIT = 27;

function nightShare(
  time: number,
  tempo: Tempo,
  player: Pick<PlayerState, 'clock'>,
  halfBars: number,
  window: number,
): number {
  let nights = 0;
  for (let sample = 0; sample < SAMPLES; sample += 1) {
    const at = time - (window * sample) / (SAMPLES - 1);
    nights += readClock(at, tempo, player.clock, halfBars).night ? 1 : 0;
  }
  return nights / SAMPLES;
}

function moonShare(
  time: number,
  tempo: Tempo,
  player: PlayerState,
  attack: Pick<CircadianAttack, 'halfBars'>,
): number {
  let lit = 0;
  for (let sample = 0; sample < SAMPLES; sample += 1) {
    const at = time - (MOON_TICKS * sample) / (SAMPLES - 1);
    lit += skyOf(at, tempo, player, attack).moon ? 1 : 0;
  }
  return lit / SAMPLES;
}

function createCircadianLook(
  bodies: Container,
  glows: Container,
  textures: Textures,
  parts: Parts,
): Look {
  const attack = CIRCADIEN.attack.kind === 'circadian' ? CIRCADIEN.attack : undefined;
  if (attack === undefined) {
    throw new Error('the Circadien has no clock');
  }
  const leftHand = add(bodies, parts.hand);
  const rightHand = add(bodies, parts.hand);
  const outline = add(bodies, parts.silhouette);
  const ponchoSun = add(bodies, parts.ponchoSun);
  const ponchoNight = add(bodies, parts.ponchoNight);
  const headdress = add(bodies, parts.headdress);
  const head = add(bodies, parts.head);
  const hair = add(bodies, parts.hair);
  const crescentGlows = Array.from({ length: MAX_CRESCENTS }, () => add(glows, textures.halo));
  const crescents = Array.from({ length: MAX_CRESCENTS }, () => add(bodies, parts.crescent));
  const dialFace = add(bodies, parts.dialFace);
  const dialSun = add(bodies, parts.dialSun);
  const dialMoon = add(bodies, parts.dialMoon);
  const dialRing = new Graphics();
  bodies.addChild(dialRing);
  const body = [leftHand, rightHand, ponchoSun, ponchoNight, headdress, head, hair, dialFace];
  let originX = 0;
  let originY = 0;
  let cos = 1;
  let sin = 0;
  let size = 1;
  let facing = 0;

  function put(sprite: Sprite, along: number, across: number, turn = 0): void {
    sprite.position.set(
      originX + (along * cos - across * sin) * UNIT * size,
      originY + (along * sin + across * cos) * UNIT * size,
    );
    sprite.rotation = facing + turn;
    sprite.scale.set(size);
  }

  return {
    hide() {
      hide(...body, outline, dialSun, dialMoon, ...crescents, ...crescentGlows);
      dialRing.visible = false;
    },
    place(input) {
      const { player, x, y, angle, scale, color, frame } = input;
      const { now, calm, tempo, light } = frame;
      const beats = now / tempo.ticksPerBeat;
      const time = frame.portrait ? now : now - 1;
      const halfBars = frame.portrait ? PORTRAIT_HALF_BARS : attack.halfBars;
      originX = x;
      originY = y;
      cos = Math.cos(angle);
      sin = Math.sin(angle);
      size = scale;
      facing = angle;
      for (const sprite of body) {
        sprite.visible = true;
      }

      const sway = calm ? 0 : Math.sin(beats * Math.PI) * HANDS.sway;
      put(leftHand, HANDS.along + sway, -HANDS.across);
      put(rightHand, HANDS.along - sway, HANDS.across);
      setTint(leftHand, input.teint);
      setTint(rightHand, input.teint);
      put(ponchoSun, 0, 0);
      setTint(ponchoSun, color);
      put(ponchoNight, 0, 0);
      placeOutline(outline, ponchoSun, parts.silhouette.texture, REFERENCE_RADIUS, frame);

      const turnTicks = calm ? TURN_TICKS.calm : TURN_TICKS.quick;
      const night = smoothstep(nightShare(time, tempo, player, halfBars, turnTicks));
      put(headdress, HEADDRESS.along, 0, night * Math.PI);
      put(head, HEAD.along, 0);
      setTint(head, input.teint);
      put(hair, HEAD.hairAlong, 0);

      const unit = UNIT * size;
      const dialX = x + DIAL_AT.x * unit;
      const dialY = y + DIAL_AT.y * unit;
      for (const sprite of [dialFace, dialSun, dialMoon]) {
        sprite.position.set(dialX, dialY);
        sprite.rotation = 0;
        sprite.scale.set(size);
      }
      dialSun.visible = night < 1;
      dialSun.alpha = 1 - night;
      dialMoon.visible = night > 0;
      dialMoon.alpha = night;
      const reading = readClock(time, tempo, player.clock, halfBars);
      const share = reading.into / (halfBars * tempo.ticksPerBar);
      const radius = DIAL_AT.ring * unit;
      const start = -Math.PI / 2;
      dialRing.visible = true;
      dialRing.position.set(dialX, dialY);
      dialRing.clear();
      dialRing
        .circle(0, 0, radius)
        .stroke({ width: DIAL_AT.width * unit, color: 0x060a1c, alpha: 0.6 });
      dialRing
        .moveTo(Math.cos(start) * radius, Math.sin(start) * radius)
        .arc(0, 0, radius, start, start + TAU * Math.max(0.001, share))
        .stroke({
          width: DIAL_AT.width * unit,
          color: reading.night ? MOON_TINT : SUN_TINT,
          alpha: 1,
          cap: 'round',
        });

      const count = frame.portrait
        ? attack.crescents.count
        : starCount(player, attack.crescents.count);
      const lit = moonShare(time, tempo, player, { halfBars });
      const orbit = frame.portrait
        ? PORTRAIT_ORBIT * unit
        : (attack.crescents.orbit * size * REFERENCE_RADIUS) / player.radius;
      crescents.forEach((crescent, rank) => {
        const glow = crescentGlows[rank];
        if (glow === undefined || rank >= count || lit <= 0) {
          crescent.visible = false;
          if (glow !== undefined) {
            glow.visible = false;
          }
          return;
        }
        const way = crescentWay(time, tempo, attack.crescents, rank, count);
        const cx = x + way.x * orbit;
        const cy = y + way.y * orbit;
        crescent.visible = glow.visible = true;
        crescent.position.set(cx, cy);
        crescent.rotation = Math.atan2(way.y, way.x) + Math.PI / 2;
        crescent.scale.set(size);
        crescent.alpha = lit;
        glow.position.set(cx, cy);
        glow.scale.set((CRESCENT.glow * unit) / textures.halo.radius);
        glow.alpha = CRESCENT_GLOW * lit * light.haloAlpha;
        setTint(glow, MOON_TINT);
      });
      return 0;
    },
  };
}

export const CIRCADIEN_DRAWING: ClassDrawing = {
  classId: 'circadien',
  paint(density): PaintedDrawing {
    const { shapes, source } = paintSheet(CIRCADIEN_PIECES, density);
    return {
      extent: EXTENT,
      height: 1,
      centerAhead: MIDDLE,
      shadow: shapes.shadow,
      source,
      create: (bodies: Container, glows: Container, textures: Textures) =>
        createCircadianLook(bodies, glows, textures, shapes),
    };
  },
};
